import { Observable, from, interval, throwError } from 'rxjs';
import {
  HttpClient,
  HttpContext,
  HttpParams,
  HttpResponse,
} from '@angular/common/http';
import {
  ClientStore,
  ClientStoreRequest,
  StoreSearchCriteria,
  QRCodeResponse,
  StoreCatalog,
  SaveOfferPositionView,
  SaveOfferView,
  SaveCategoryView,
  CategoriesListResponse,
  User,
  StoreSettingUpdateRequest,
  StoreStatistics,
  Lang,
  StoreZone,
  StoreZoneStatus,
  OrderItemXlsResponse,
  KeyValuePair,
  MynextLoginResponse,
  StoreOrderItemsStatistics,
  NotificationStatisticsResponse,
  Customer,
  CustomersListDownloadResponse,
  PowersoftLoginResponse,
  PowersoftLoginRequest,
  StoreRelationRequest,
  Relation,
  CustomEndPointValidationRequest,
  CustomerBasic,
  CustomerOrderSummary,
  CustomerReservationStatistics,
  CustomerLoyaltyProfile,
  CustomerOrderItems,
  CustomerAssignedVoucher,
  CustomerForm,
  CustomerRecentOrders,
  CustomerLoyaltyTrasactions,
  CustomerOrderItem,
  CustomerReservation,
  UploadResponse,
  CustomerLoyaltyProfileUpdate,
  CustomerLoyaltyProfileUpdateData,
  WoltLoginRequest,
  OrderItem,
  CustomerLoyaltyTrasaction,
  SaveZonePositionView,
  Order,
  StoreBalancePayload,
  Product,
  ProductListResponse,
  ProductSearchResponse,
  ProductResponse,
  ProductRequest,
} from './stores';
import { Injectable } from '@angular/core';
import { PageableResults, Paging, defaultPaging } from '../api/types/Pageable';
import {
  tap,
  map,
  take,
  switchMap,
  takeWhile,
  catchError,
} from 'rxjs/operators';
import { AliasAvailabilityStatus } from './+state/stores.reducer';
import { CatalogFeedPlatform } from './types/CatalogFeed';
import { environment } from 'src/environments/environment';
import { HelperService } from '../public/helper.service';
import { PrinterService } from '../printers/printer.service';
import { MobilePushNotificationService } from '../shared/mobile-push-notification.service';
import { Page } from '../api/types/Page';
import { MobileStorageService } from './mobile-storage.service';
import { Store } from '@ngrx/store';
import { IntegrationProvider } from '../public/store/types/IntegrationPayment';
import { ExportTaskResponse, ExportTaskStatus } from './export-task.model';
import { CustomerSearchRequest, CustomerType } from './customer-search.model';

@Injectable({
  providedIn: 'root',
})
export class StoresService {
  constructor(
    private http: HttpClient,
    private helperService: HelperService,
    private printerService: PrinterService,
    private mobilePushNotificationService: MobilePushNotificationService,
    private mobileStorageService: MobileStorageService,
    private store: Store,
  ) {
    this.init();
  }

  /**
   * Register a refund for a store. Payload should include orderToken/orderId, amount, paymentMethod and reason.
   */
  registerRefund(storeId: number, payload: any) {
    // Endpoint follows existing API patterns used in the app. Adjust path if backend differs.
    return this.http.post<any>(`/api/v1/stores/${storeId}/refunds`, payload);
  }

  /**
   * Get refunds for a store.
   */
  getRefunds(storeId: number, paging?: { page: number; size: number }) {
    const params: any = {};
    if (paging) {
      params.page = paging.page.toString();
      params.size = paging.size.toString();
    }
    return this.http.get<any>(`/api/v1/stores/${storeId}/refunds`, { params });
  }

  /**
   * Get balance summary for a store for a specific reference date (UTC yyyy-MM-dd)
   */
  getBalanceSummary(storeId: number, referenceDate: string) {
    return this.http.get<any>(
      `/api/store/${storeId}/balance/summary/${referenceDate}`,
    );
  }

  /**
   * Get the latest balance summary for a store
   */
  getLatestBalanceSummary(storeId: number) {
    return this.http.get<any>(`/api/store/${storeId}/balance/summary/latest`);
  }

  /**
   * Open balance for a store for a reference day
   */
  openStoreBalance(
    storeId: number,
    payload: {
      openingBalanceCash: number;
      openingBalanceCard: number;
      openingBalanceOnline: number;
      referenceDay: string; // yyyy-MM-dd
    },
  ) {
    return this.http.post<any>(`/api/store/${storeId}/balance/open`, payload);
  }

  /**
   * Get closing day details for a store and reference date
   */
  getStoreBalanceDetails(storeId: number, referenceDate: string) {
    return this.http.get<any>(
      `/api/store/${storeId}/balance/details/${referenceDate}`,
    );
  }

  /**
   * Get daily balance report list for a date range (balance statistics)
   * Backend: StoreBalanceController.getDailyReport
   * Expected path shape based on other balance routes
   */
  getBalanceDailyReport(storeId: number, fromDate: string, toDate: string) {
    // fromDate/toDate should be yyyy-MM-dd (UTC) or ISO date strings as backend expects
    // Backend returns a paginated object with a 'data' array
    return this.http.get<any>(`/api/store/${storeId}/balance/reports/daily`, {
      params: { fromDate: fromDate, toDate: toDate },
    });
  }

  /**
   * Get monthly balance report list for a date range (balance statistics monthly)
   * Backend: StoreBalanceController.getMonthlyReport
   */
  getBalanceMonthlyReport(storeId: number, fromDate: string, toDate: string) {
    return this.http.get<any>(`/api/store/${storeId}/balance/reports/monthly`, {
      params: { fromDate: fromDate, toDate: toDate },
    });
  }

  getOutboundNotificationStatistics(
    storeId: number,
    from: string,
    to: string,
    period: 'DAILY' | 'MONTHLY',
  ): Observable<NotificationStatisticsResponse> {
    return this.http.get<NotificationStatisticsResponse>(
      `/api/v1/stores/${storeId}/notifications/outbound/statistics`,
      {
        params: { from, to, period },
      },
    );
  }

  /**
   * Update balance for a store and reference date without closing the business day
   */
  updateStoreBalance(
    storeId: number,
    referenceDate: string,
    payload: StoreBalancePayload,
  ) {
    return this.http.put<any>(
      `/api/store/${storeId}/balance/${referenceDate}`,
      payload,
    );
  }

  /**
   * Close balance for a store and reference date
   */
  closeStoreBalance(
    storeId: number,
    referenceDate: string,
    payload: StoreBalancePayload,
  ) {
    return this.http.post<any>(
      `/api/store/${storeId}/balance/close/${referenceDate}`,
      payload,
    );
  }

  /**
   * Export daily summary report
   */
  exportDailySummary(
    storeId: number,
    referenceDate: string,
  ): Observable<HttpResponse<Blob>> {
    return this.http.get(
      `/api/store/${storeId}/balance/export/${referenceDate}`,
      {
        observe: 'response',
        responseType: 'blob',
      },
    );
  }

  async init() {
    if (this.helperService.isGoAdminApp()) {
      setTimeout(() => {
        from(this.mobileStorageService.get('autoprintSubscriptions'))
          .pipe(take(1))
          .subscribe((subscriptions) => {
            this.printerService.init(subscriptions);
          });
        from(this.mobileStorageService.get('notificationSubscriptions'))
          .pipe(take(1))
          .subscribe((subscriptions) => {
            this.mobilePushNotificationService.init(subscriptions);
          });
      }, 1000);
    }
  }

  list(
    paging = defaultPaging,
    aliasName: string,
    viewType?: string,
    currentStoreId?: number,
    deletedState?: string,
  ): Observable<PageableResults<ClientStore>> {
    let params = new HttpParams()
      .set('aliasName', aliasName || '')
      .set('page', paging.page.toString())
      .set('size', paging.size.toString());

    if (viewType) {
      params = params.set('viewType', viewType);
    }

    if (currentStoreId !== undefined) {
      params = params.set('currentStoreId', currentStoreId.toString());
    }

    if (deletedState) {
      params = params.set('deletedState', deletedState);
    }

    return this.http.get<PageableResults<ClientStore>>(
      '/api/v1/stores/search',
      { params },
    );
  }

  load(id: number): Observable<ClientStore> {
    return this.http.get<ClientStore>(`/api/v1/stores/${id}`);
  }

  createV2(
    store: ClientStoreRequest,
    captchaToken: string,
  ): Observable<ClientStore> {
    return this.http.post<ClientStore>('/api/v2/stores', {
      ...store,
      captchaToken,
    });
  }

  update(id: number, store: ClientStoreRequest): Observable<ClientStore> {
    return this.http.put<ClientStore>(`/api/v1/stores/${id}`, store);
  }

  partialUpdate(
    id: number,
    store: ClientStoreRequest,
  ): Observable<ClientStore> {
    return this.http.patch<ClientStore>(`/api/v1/stores/${id}`, store);
  }

  deleteStore(storeId): Observable<any> {
    return this.http.delete<any>(`/api/v1/stores/${storeId}/delete`);
  }

  deleteStoreParent(storeId): Observable<any> {
    return this.http.delete<any>(`/api/v1/stores/${storeId}/relation`);
  }

  updateStoreVatPercentage(
    id: number,
    vatPercentage: number,
  ): Observable<ClientStore> {
    return this.http.patch<ClientStore>(`/api/v1/stores/${id}`, {
      vatPercentage,
    });
  }

  updateStoreTenant(id: number, tenantCode: string): Observable<ClientStore> {
    return this.http.patch<ClientStore>(`/api/v1/stores/${id}`, {
      tenantCode,
    });
  }

  downloadCatalogFeed(
    storeId: number,
    platform: CatalogFeedPlatform,
  ): Observable<Blob> {
    return this.http.get(`/api/v1/stores/${storeId}/catalog-feed/${platform}`, {
      responseType: 'blob',
    });
  }

  updateSettings(
    id: number,
    settings: StoreSettingUpdateRequest[],
  ): Observable<ClientStore> {
    return this.http.put<ClientStore>(
      `/api/v1/stores/${id}/settings`,
      settings,
    );
  }

  downloadQRImage(id: number, url: string): Observable<QRCodeResponse> {
    return this.http
      .post(
        `/api/v1/stores/${id}/qr-generate-url`,
        { url },
        {
          observe: 'response',
          responseType: 'blob',
        },
      )
      .pipe(map((r) => this.toQRCodeResponse(r)));
  }

  downloadQRImageForData(id: number, data: string): Observable<QRCodeResponse> {
    return this.http
      .post(
        `/api/v1/stores/${id}/qr-generate-data`,
        { data },
        {
          observe: 'response',
          responseType: 'blob',
        },
      )
      .pipe(map((r) => this.toQRCodeResponse(r)));
  }

  downloadPublicVoucherQRImage(
    id: number,
    code: string,
  ): Observable<QRCodeResponse> {
    return this.http
      .get(
        `/api/v1/stores/${id}/customer-voucher/${encodeURIComponent(code)}/qr-image`,
        {
          observe: 'response',
          responseType: 'blob',
        },
      )
      .pipe(map((r) => this.toQRCodeResponse(r)));
  }

  downloadOrRenderFlyerImage(
    id: number,
    voucherCode: string,
    toImage: boolean = false,
  ): Observable<QRCodeResponse> {
    let params = new HttpParams();
    if (voucherCode.length > 0) {
      params = params.set('voucherCode', voucherCode);
    }
    if (toImage) {
      params = params.set('toImage', String(toImage));
    }
    return this.http
      .get(`/api/v2/stores/${id}/flyer`, {
        observe: 'response',
        params,
        responseType: 'blob',
      })
      .pipe(
        map((r) =>
          this.toQRCodeResponse(
            r,
            toImage ? 'store-flyer.png' : 'store-flyer.pdf',
          ),
        ),
      );
  }

  downloadQRImageFull(id: number): Observable<QRCodeResponse> {
    return this.http
      .get(`/api/v1/stores/${id}/qr-image?full-version=true`, {
        observe: 'response',
        responseType: 'blob',
      })
      .pipe(map((r) => this.toQRCodeResponse(r)));
  }

  downloadQRPdf(id: number): Observable<QRCodeResponse> {
    return this.http
      .get(`/api/v1/stores/${id}/qr-pdf`, {
        observe: 'response',
        responseType: 'blob',
      })
      .pipe(map((r) => this.toQRCodeResponse(r)));
  }

  downloadQRImages(id: number): Observable<QRCodeResponse> {
    return this.http
      .get(`/api/v1/stores/${id}/qr-images`, {
        observe: 'response',
        responseType: 'blob',
      })
      .pipe(map((r) => this.toQRCodeResponse(r)));
  }

  downloadQRFullPdf(id: number): Observable<QRCodeResponse> {
    return this.http
      .get(`/api/v1/stores/${id}/qr-pdf-all`, {
        observe: 'response',
        responseType: 'blob',
      })
      .pipe(map((r) => this.toQRCodeResponse(r)));
  }

  downloadOrderItemXls(
    storeId: number,
    orderItemReportType: string,
    fromDate: string,
    toDate: string,
    showAllOrderItems?: boolean,
  ): Observable<OrderItemXlsResponse> {
    // Mirror the on-screen list path (orderItemsStatisticsList): the wire flag is the inverse of
    // the "Show statistics for all items" checkbox, so the download scope matches the table.
    const parentOrderItemsOnly = !showAllOrderItems;
    return this.http
      .get(`/api/v1/stores/${storeId}/orders/items`, {
        observe: 'response',
        responseType: 'blob',
        params: {
          orderItemReportType,
          orderItemDateType: 'ORDER_DATE',
          startDate: fromDate,
          endDate: toDate,
          storeId: storeId.toString(),
          parentOrderItemsOnly: parentOrderItemsOnly.toString(),
        },
      })
      .pipe(map((r) => this.toOrderItemXls(r)));
  }

  private toQRCodeResponse(
    response: HttpResponse<Blob>,
    fallbackFilename = 'download',
  ): QRCodeResponse {
    const blob = response.body;
    const contentDisposition = response.headers.get('content-disposition');
    if (blob == null) {
      throw new Error('Downloaded file body is empty');
    }
    const filename = contentDisposition?.includes('filename=')
      ? contentDisposition
          .slice(contentDisposition.indexOf('filename=') + 9)
          .replace(/"/g, '')
      : fallbackFilename;
    return { blob, filename };
  }

  private toOrderItemXls(response: HttpResponse<Blob>): OrderItemXlsResponse {
    const blob = response.body;
    const contentDisposition = response.headers.get('content-disposition');
    const filename = contentDisposition
      .slice(contentDisposition.indexOf('filename=') + 9)
      .replace(/"/g, '');
    return { blob, filename };
  }

  getcatalog(id: string): Observable<StoreCatalog> {
    return this.http.get<StoreCatalog>(`/api/v1/stores/${id}/catalog`);
  }

  saveOfferPosition(
    offers: SaveOfferPositionView,
  ): Observable<SaveOfferPositionView> {
    return this.http.post<SaveOfferPositionView>(
      '/api/v1/stores/catalog/offer/position',
      offers,
    );
  }

  loadOffer(id: string): Observable<SaveOfferView> {
    return this.http.get<SaveOfferView>(`/api/v1/stores/catalog/offer/${id}`);
  }

  saveCategory(
    category: SaveCategoryView,
    categoryId: any,
  ): Observable<SaveCategoryView> {
    if (categoryId === 'CREATE_CATEGORY') {
      return this.http.post<SaveCategoryView>(
        '/api/v1/stores/catalog/category',
        category,
      );
    } else {
      return this.http.post<SaveCategoryView>(
        `/api/v1/stores/catalog/category/${categoryId}`,
        category,
      );
    }
  }

  loadCategory(id: string): Observable<SaveCategoryView> {
    return this.http.get<SaveCategoryView>(
      `/api/v1/stores/catalog/category/${id}`,
    );
  }

  listCatalogCategories(catalogId: number): Observable<CategoriesListResponse> {
    return this.http.get<CategoriesListResponse>(
      `/api/v1/stores/catalog/${catalogId}/categories/name`,
    );
  }

  userList(
    id: number,
    paging = defaultPaging,
  ): Observable<PageableResults<User>> {
    return this.http.get<PageableResults<User>>(
      `/api/v1/users/${id}?page=${paging.page}&size=${paging.size}`,
    );
  }

  inviteUser(email: string, role: string, storeId: any): Observable<{}> {
    const request = {
      email,
      role,
      storeId,
    };
    return this.http.post<{}>('/api/v1/users/invite', request);
  }

  removeUserStoreAccess(userId, storeId): Observable<void> {
    return this.http.delete<void>(`/api/v1/users/${userId}/store/${storeId}`);
  }

  removeStoreLogo(storeId): Observable<void> {
    return this.http.delete<void>(`/api/v1/stores/${storeId}/delete-logo`);
  }

  removeStoreImage(storeId, isBackgroundImage = false): Observable<void> {
    return this.http.delete<void>(
      `/api/v2/stores/${storeId}/${isBackgroundImage ? 'delete-background' : 'delete-image'}`,
    );
  }

  uploadStoreImage(
    storeId: number,
    file: File,
    isBackgroundImage = false,
  ): Observable<UploadResponse> {
    const formData = new FormData();
    formData.append('file', file);
    return this.http.post<UploadResponse>(
      `/api/v2/stores/${storeId}/${isBackgroundImage ? 'upload-background' : 'upload-image'}`,
      formData,
    );
  }

  uploadStoreLogo(storeId: number, file: File): Observable<UploadResponse> {
    const formData = new FormData();
    formData.append('file', file);
    return this.http.post<UploadResponse>(
      `/api/v2/stores/${storeId}/upload-logo`,
      formData,
    );
  }

  searchStores(
    criteria: StoreSearchCriteria,
    paging = { page: 0, size: 10 },
  ): Observable<PageableResults<ClientStore>> {
    let params = new HttpParams()
      .set('page', paging.page.toString())
      .set('size', paging.size.toString());

    // Exactly one of id / aliasName is ever sent — never both. Sending an empty
    // aliasName alongside an id would add a match-everything criterion server-side.
    if (criteria.id !== undefined && criteria.id !== null) {
      params = params.set('id', criteria.id.toString());
    } else {
      params = params.set('aliasName', criteria.alias ?? '');
    }

    if (criteria.currentStoreId) {
      params = params.set('currentStoreId', criteria.currentStoreId.toString());
    }

    if (criteria.viewType) {
      params = params.set('viewType', criteria.viewType);
    }

    if (criteria.deletedState) {
      params = params.set('deletedState', criteria.deletedState);
    }

    return this.http.get<PageableResults<ClientStore>>(
      '/api/v1/stores/search',
      { params },
    );
  }

  loadStoreStatistics(
    id: number,
    duration: number,
    from: string,
    to: string,
    periodicTerm: string,
  ): Observable<StoreStatistics[]> {
    return this.http.get<StoreStatistics[]>(
      `/api/v1/stores/${id}/order/stats?from=${from}&to=${to}&duration=${duration}&periodicTerm=${periodicTerm}`,
    );
  }

  loadMenuViewingStatistics(
    id: number,
    duration: number,
    from: string,
    to: string,
    periodicTerm: string,
  ): Observable<StoreStatistics[]> {
    return this.http.get<StoreStatistics[]>(
      `/api/v1/stores/${id}/order/statsmenuview?from=${from}&to=${to}&duration=${duration}&periodicTerm=${periodicTerm}`,
    );
  }

  loadLoyaltyStatistics(
    storeId: number,
    from: string,
    to: string,
    period: string,
    groupBy: string,
  ): Observable<any> {
    return this.http.get<any>(
      `/api/v1/stores/${storeId}/loyalty/statistics?from=${from}&to=${to}&period=${period}&groupBy=${groupBy}`,
    );
  }

  downloadOrderPdf(id: number, orderId: string): Observable<QRCodeResponse> {
    return this.http
      .get(`/api/v1/stores/${id}/orders/${orderId}/invoice`, {
        observe: 'response',
        responseType: 'blob',
      })
      .pipe(map((r) => this.toQRCodeResponse(r)));
  }

  orderItemsStatisticsList(
    storeId: number,
    startDate: string,
    endDate: string,
    orderItemDateType: string,
    paging = defaultPaging,
    sort: string,
    showAllOrderItems?: boolean,
  ): Observable<PageableResults<StoreOrderItemsStatistics>> {
    const parentOrderItemsOnly = !showAllOrderItems;
    const storeApiUrl = `/api/v1/stores/${storeId}/orders/statistics/items?startDate=${startDate}&endDate=${endDate}&orderItemDateType=${orderItemDateType}&page=${paging.page}&size=${paging.size}&sort=${sort}&parentOrderItemsOnly=${parentOrderItemsOnly}`;
    return this.http.get<PageableResults<StoreOrderItemsStatistics>>(
      storeApiUrl,
    );
  }

  getUserInterfaceLanguages() {
    return this.http.get<Lang[]>(
      `/assets/user-interface-languages/user-interface-languages.json`,
    );
  }

  validateAliasAvailability(
    storeId: number,
    alias: string,
  ): Observable<AliasAvailabilityStatus> {
    const request = {
      storeId,
      alias,
    };
    return this.http.post<AliasAvailabilityStatus>(
      `/api/v1/stores/alias/available`,
      request,
    );
  }

  loadNotificationSubscriptionStatus(
    storeId: number,
    subscription: PushSubscription,
  ): Observable<boolean> {
    console.log('load notification status call to backend');
    return this.http.get<boolean>(
      `/api/v1/stores/${storeId}/notifications/${subscription.toJSON().keys.p256dh}`,
    );
  }

  subscribeNotification(
    storeId: number,
    subscription: PushSubscription,
  ): Observable<void> {
    console.log('subscribe call to backend');
    return this.http.post<void>(
      `/api/v1/stores/${storeId}/notifications`,
      subscription.toJSON(),
    );
  }

  unsubscribeNotification(
    storeId: number,
    subscription: PushSubscription,
  ): Observable<void> {
    console.log('unsubscribe call to backend');
    return this.http.delete<void>(
      `/api/v1/stores/${storeId}/notifications/${subscription.toJSON().keys.p256dh}`,
    );
  }

  createOrUpdateZone(
    storeId: number,
    zone: StoreZone,
    id: number = 0,
  ): Observable<StoreZone> {
    if (id === 0) {
      return this.http.post<StoreZone>(`/api/v2/stores/${storeId}/zones`, zone);
    } else {
      return this.http.put<StoreZone>(
        `/api/v2/stores/${storeId}/zones/${id}`,
        zone,
      );
    }
  }

  getZone(id: number): Observable<StoreZone> {
    return this.http.get<StoreZone>(`/api/v1/stores/${id}/zones/${id}`);
  }

  getStatusOfZone(id: number): Observable<StoreZoneStatus> {
    return this.http.get<StoreZoneStatus>(`/api/v1/stores/${id}/zones/status`);
  }

  loadZones(id: number): Observable<StoreZone[]> {
    return this.http.get<StoreZone[]>(`/api/v2/stores/${id}/zones`);
  }

  loadZone(storeId: number, id: number): Observable<StoreZone> {
    return this.http.get<StoreZone>(`/api/v2/stores/${storeId}/zones/${id}`);
  }

  removeZone(zoneId, storeId): Observable<void> {
    return this.http.delete<void>(`/api/v2/stores/${storeId}/zones/${zoneId}`);
  }

  updateZoneSettings(
    zoneId,
    storeId,
    settings: KeyValuePair[],
  ): Observable<StoreZone> {
    return this.http.put<StoreZone>(
      `/api/v1/stores/${storeId}/zones/${zoneId}/settings`,
      settings,
    );
  }

  setAllowOrdering(lang, allowOrderingStatus): Observable<any> {
    return this.http.get<any>(
      `/api/v1/stores/?ulang=${lang}&basket=${allowOrderingStatus}`,
    );
  }

  mynextLogin(
    username: string,
    password: string,
    storeId: number,
    venueId,
    webhookApiKey?: string,
  ): Observable<ClientStore> {
    const payload = {
      username,
      password,
    };
    if (venueId) payload['venueId'] = venueId;
    if (webhookApiKey) payload['webhookApiKey'] = webhookApiKey;
    if (venueId) payload['venueId'] = venueId;
    return this.http.post<ClientStore>(
      `/api/v2/stores/${storeId}/integrations/mynext/connect`,
      payload,
    );
  }

  disconnectMynext(storeId: number): Observable<any> {
    return this.http.post<any>(
      `/api/v2/stores/${storeId}/integrations/mynext/disconnect`,
      {},
    );
  }

  powersoftLogin(
    storeId: number,
    reqObj: PowersoftLoginRequest,
  ): Observable<PowersoftLoginResponse> {
    return this.http.post<PowersoftLoginResponse>(
      `/api/v2/stores/${storeId}/powersoft/connect`,
      { ...reqObj },
    );
  }

  woltLogin(storeId: number, reqObj: WoltLoginRequest): Observable<any> {
    return this.http.post<any>(`/api/v2/stores/${storeId}/wolt/connect`, {
      ...reqObj,
    });
  }

  disconnectWolt(storeId: number): Observable<any> {
    return this.http.post<any>(`/api/v2/stores/${storeId}/wolt/disconnect`, {});
  }

  syncWoltCatalog(storeId: number): Observable<any> {
    return this.http.post<any>(
      `/api/v2/stores/${storeId}/wolt/catalog/import`,
      {},
    );
  }

  disconnectPowersoft(storeId: number): Observable<any> {
    return this.http.post<any>(
      `/api/v2/stores/${storeId}/powersoft/disconnect`,
      {},
    );
  }

  synchronizePowersoftCatalog(storeId: number): Observable<any> {
    return this.http.post<any>(
      `/api/v2/stores/${storeId}/powersoft/catalog/import`,
      {},
    );
  }

  synchronizePosterCatalog(storeId: number): Observable<any> {
    return this.http.post<any>(
      `/api/v2/stores/${storeId}/joinposter/catalog/import`,
      {},
    );
  }

  hubriseLogin(storeId: number, authCode: string): Observable<ClientStore> {
    return this.http.post<ClientStore>(
      `/api/v2/stores/${storeId}/hubrise/connect`,
      { authCode },
    );
  }

  hubriseLogout(storeId: number): Observable<ClientStore> {
    return this.http.post<ClientStore>(
      `/api/v2/stores/${storeId}/hubrise/disconnect`,
      {},
    );
  }

  customersList(
    storeId: number,
    email: string,
    name: string,
    phoneNumber: string,
    sortingColumn: string,
    paging = defaultPaging,
    customerCode: string,
    birthdayFrom?: string,
    birthdayTo?: string,
    customerType?: CustomerType,
    context?: HttpContext,
  ): Observable<PageableResults<Customer>> {
    const requestBody: Record<string, string> = {};
    if (email) {
      requestBody.email = email;
    }
    if (name) {
      requestBody.name = name;
    }
    if (phoneNumber) {
      requestBody.phoneNumber = phoneNumber;
    }
    if (customerCode) {
      requestBody.memberCode = customerCode;
    }
    if (birthdayFrom) {
      requestBody.birthdayFrom = birthdayFrom;
    }
    if (birthdayTo) {
      requestBody.birthdayTo = birthdayTo;
    }
    if (customerType && customerType !== 'ALL') {
      requestBody.customerType = customerType;
    }
    const sortParam = sortingColumn ? `sort=${sortingColumn}&` : '';
    return this.http.post<PageableResults<Customer>>(
      `/api/v1/stores/${storeId}/customers/search-all?${sortParam}page=${paging.page}&size=${paging.size}`,
      requestBody,
      { context },
    );
  }

  topOrderCustomers(
    storeId: number,
    paging = defaultPaging,
  ): Observable<PageableResults<Customer>> {
    return this.http.get<PageableResults<Customer>>(
      `/api/v1/stores/${storeId}/customers/top-orders?page=${paging.page}&size=${paging.size}`,
    );
  }

  topReservationCustomers(
    storeId: number,
    paging = defaultPaging,
  ): Observable<PageableResults<Customer>> {
    return this.http.get<PageableResults<Customer>>(
      `/api/v1/stores/${storeId}/customers/top-reservations?page=${paging.page}&size=${paging.size}`,
    );
  }

  parseTopOrderCustomerToCustomerResponse(response) {
    const responseObj = response ? { ...response } : {};
    return {
      data: responseObj.content || [],
      totalPages: responseObj.totalPages || 0,
      totalElements: responseObj.totalElements || 0,
      pageNumber: responseObj.number || 0,
      pageSize: responseObj.size || 0,
    };
  }

  downloadCustomersList(
    storeId: number,
    email: string,
    name: string,
    phoneNumber: string,
  ): Observable<CustomersListDownloadResponse> {
    const request = {
      email,
      name,
      phoneNumber,
    };
    return this.http
      .post(
        `/api/v1/stores/${storeId}/customers/download`,
        email.length === 0 && name.length === 0 && phoneNumber.length === 0
          ? {}
          : request,
        { observe: 'response', responseType: 'blob' },
      )
      .pipe(map((r) => this.toCustomerSearch(r)));
  }

  // ==================== NEW ASYNC EXPORT METHODS ====================

  /**
   * Initiates an async customer export.
   * @param storeId  the store to export
   * @param searchRequest  optional search/filter criteria to apply to the export (mirrors the
   *   current state of the Customers page — customer type, name, email, phone, etc.)
   */
  initiateCustomerExport(
    storeId: number,
    searchRequest?: CustomerSearchRequest,
  ): Observable<ExportTaskResponse> {
    const body: Partial<CustomerSearchRequest> = {};
    if (searchRequest?.customerType && searchRequest.customerType !== 'ALL') {
      body.customerType = searchRequest.customerType;
    }
    if (searchRequest?.name) {
      body.name = searchRequest.name;
    }
    if (searchRequest?.email) {
      body.email = searchRequest.email;
    }
    if (searchRequest?.phoneNumber) {
      body.phoneNumber = searchRequest.phoneNumber;
    }
    if (searchRequest?.customerCode) {
      body.customerCode = searchRequest.customerCode;
    }
    return this.http.post<ExportTaskResponse>(
      `/api/v1/stores/${storeId}/customers/export/initiate`,
      body,
    );
  }

  getExportStatus(
    storeId: number,
    exportId: number,
  ): Observable<ExportTaskResponse> {
    return this.http.get<ExportTaskResponse>(
      `/api/v1/stores/${storeId}/customers/export/${exportId}/status`,
    );
  }

  pollExportStatus(
    storeId: number,
    exportId: number,
    pollInterval: number = 2000,
  ): Observable<ExportTaskResponse> {
    return interval(pollInterval).pipe(
      switchMap(() => this.getExportStatus(storeId, exportId)),
      takeWhile(
        (response) =>
          response.status === ExportTaskStatus.PENDING ||
          response.status === ExportTaskStatus.PROCESSING,
        true,
      ),
    );
  }

  downloadCustomerExportFile(
    storeId: number,
    exportId: number,
  ): Observable<Blob> {
    return this.http
      .get(`/api/v1/stores/${storeId}/customers/export/${exportId}/download`, {
        responseType: 'blob',
        observe: 'response',
      })
      .pipe(map((response) => response.body!));
  }

  exportCustomersAsync(
    storeId: number,
    onProgress?: (status: ExportTaskResponse) => void,
    searchRequest?: CustomerSearchRequest,
  ): Observable<{ blob: Blob; fileName: string }> {
    return this.initiateCustomerExport(storeId, searchRequest).pipe(
      switchMap((initialResponse) => {
        if (onProgress) {
          onProgress(initialResponse);
        }

        return this.pollExportStatus(storeId, initialResponse.id).pipe(
          tap((status) => {
            if (onProgress) {
              onProgress(status);
            }
          }),
          switchMap((finalStatus) => {
            if (finalStatus.status === ExportTaskStatus.COMPLETED) {
              return this.downloadCustomerExportFile(
                storeId,
                finalStatus.id,
              ).pipe(
                map((blob) => ({
                  blob,
                  fileName: finalStatus.fileName || 'customer_list.xlsx',
                })),
              );
            }

            if (
              finalStatus.status === ExportTaskStatus.FAILED ||
              finalStatus.status === ExportTaskStatus.EXPIRED
            ) {
              return this.fallbackToLegacyCustomerExport(storeId);
            }

            return throwError(
              () =>
                new Error(
                  finalStatus.errorMessage ||
                    `Customer export failed with status ${finalStatus.status}`,
                ),
            );
          }),
        );
      }),
      catchError((asyncExportError) =>
        this.fallbackToLegacyCustomerExport(storeId).pipe(
          catchError(() => throwError(() => asyncExportError)),
        ),
      ),
    );
  }

  // ==================== END NEW ASYNC EXPORT METHODS ====================

  private fallbackToLegacyCustomerExport(
    storeId: number,
  ): Observable<{ blob: Blob; fileName: string }> {
    return this.downloadCustomersList(storeId, '', '', '').pipe(
      map((downloadResponse) => ({
        blob: downloadResponse.blob,
        fileName: this.decodeCustomerExportFileName(downloadResponse.filename),
      })),
    );
  }

  private decodeCustomerExportFileName(rawFileName?: string): string {
    if (!rawFileName) {
      return 'customer_list.xlsx';
    }

    try {
      return decodeURIComponent(rawFileName);
    } catch {
      return rawFileName;
    }
  }

  private toCustomerSearch(
    response: HttpResponse<Blob>,
  ): CustomersListDownloadResponse {
    const blob = response.body;
    const contentDisposition = response.headers.get('content-disposition');
    const filename =
      contentDisposition && contentDisposition.includes('filename=')
        ? contentDisposition
            .slice(contentDisposition.indexOf('filename=') + 9)
            .replace(/\"/g, '')
        : 'customer_list.xlsx';
    return { blob, filename };
  }

  /** @deprecated - use IntegrationService.connectIntegrationPayment instead */
  connectPaymentsense(
    storeId: number,
    settings: { [key: string]: any },
  ): Observable<ClientStore> {
    return this.http.post<ClientStore>(
      `/api/v2/stores/${storeId}/paymentsense/connect`,
      settings,
    );
  }

  /** @deprecated - use IntegrationService.connectIntegrationPayment instead */
  connectJcc(
    storeId: number,
    settings: { [key: string]: any },
  ): Observable<ClientStore> {
    return this.http.post<ClientStore>(
      `/api/v2/stores/${storeId}/jcc/connect`,
      settings,
    );
  }

  /** @deprecated - use IntegrationService.disConnectIntegrationPayment instead */
  disConnectJcc(storeId: number): Observable<any> {
    return this.http.put<any>(`/api/v2/stores/${storeId}/jcc/disconnect`, {});
  }

  /** @deprecated - use IntegrationService.connectIntegrationPayment instead */
  connectTrustPayments(
    storeId: number,
    settings: { [key: string]: any },
  ): Observable<ClientStore> {
    return this.http.post<ClientStore>(
      `/api/v2/stores/${storeId}/trustpayments/connect`,
      settings,
    );
  }

  /** @deprecated - use IntegrationService.disConnectIntegrationPayment instead */
  disConnectTrustPayments(storeId: number): Observable<any> {
    return this.http.put<any>(
      `/api/v2/stores/${storeId}/trustpayments/disconnect`,
      {},
    );
  }

  /** @deprecated - use IntegrationService.connectIntegrationPayment instead */
  connectVivaPayments(
    storeId: number,
    settings: { [key: string]: any },
  ): Observable<ClientStore> {
    return this.http.post<ClientStore>(
      `/api/v2/stores/${storeId}/viva/connect`,
      settings,
    );
  }

  /** @deprecated - use IntegrationService.disConnectIntegrationPayment instead */
  disConnectVivaPayments(storeId: number): Observable<any> {
    return this.http.put<any>(`/api/v2/stores/${storeId}/viva/disconnect`, {});
  }

  /** @deprecated - use IntegrationService.connectIntegrationPayment instead */
  connectPayabl(
    storeId: number,
    settings: { [key: string]: any },
  ): Observable<ClientStore> {
    return this.http.post<ClientStore>(
      `/api/v2/stores/${storeId}/payabl/connect`,
      settings,
    );
  }

  /** @deprecated - use IntegrationService.disConnectIntegrationPayment instead */
  disConnectPayabl(storeId: number): Observable<any> {
    return this.http.put<any>(
      `/api/v2/stores/${storeId}/payabl/disconnect`,
      {},
    );
  }

  /** @deprecated - use IntegrationService.connectIntegrationPayment instead */
  connectGlobalPay(
    storeId: number,
    settings: { [key: string]: any },
  ): Observable<ClientStore> {
    return this.http.post<ClientStore>(
      `/api/v2/stores/${storeId}/globalpay/connect`,
      settings,
    );
  }

  /** @deprecated - use IntegrationService.disConnectIntegrationPayment instead */
  disConnectGlobalPay(storeId: number): Observable<ClientStore> {
    return this.http.put<ClientStore>(
      `/api/v2/stores/${storeId}/globalpay/disconnect`,
      {},
    );
  }

  /** @deprecated - use IntegrationService.connectIntegrationPayment instead */
  connectMollie(storeId: number): Observable<string> {
    return this.http.get(`/api/v2/stores/${storeId}/mollie/connect`, {
      responseType: 'text',
    });
  }

  /** @deprecated - use IntegrationService.disConnectIntegrationPayment instead */
  disConnectMollie(storeId: number): Observable<ClientStore> {
    return this.http.put<ClientStore>(
      `/api/v2/stores/${storeId}/mollie/disconnect`,
      {},
    );
  }

  /** @deprecated - use IntegrationService.connectIntegrationPayment instead */
  connectFygaro(
    storeId: number,
    settings: { [key: string]: any },
  ): Observable<ClientStore> {
    return this.http.post<ClientStore>(
      `/api/v2/stores/${storeId}/fygaro/connect`,
      settings,
    );
  }

  /** @deprecated - use IntegrationService.disConnectIntegrationPayment instead */
  disConnectFygaro(storeId: number): Observable<ClientStore> {
    return this.http.put<ClientStore>(
      `/api/v2/stores/${storeId}/fygaro/disconnect`,
      {},
    );
  }

  /** @deprecated - use IntegrationService.connectIntegrationPayment instead */
  connectEpay(
    storeId: number,
    settings: { [key: string]: any },
  ): Observable<ClientStore> {
    return this.http.post<ClientStore>(
      `/api/v2/stores/${storeId}/epay/connect`,
      settings,
    );
  }

  /** @deprecated - use IntegrationService.disConnectIntegrationPayment instead */
  disConnectEpay(storeId: number): Observable<ClientStore> {
    return this.http.put<ClientStore>(
      `/api/v2/stores/${storeId}/epay/disconnect`,
      {},
    );
  }

  /** @deprecated - use IntegrationService.connectIntegrationPayment instead */
  connectApcopay(
    storeId: number,
    settings: { [key: string]: any },
  ): Observable<ClientStore> {
    return this.http.post<ClientStore>(
      `/api/v2/stores/${storeId}/apcopay/connect`,
      settings,
    );
  }

  /** @deprecated - use IntegrationService.disConnectIntegrationPayment instead */
  disConnectApcopay(storeId: number): Observable<ClientStore> {
    return this.http.put<ClientStore>(
      `/api/v2/stores/${storeId}/apcopay/disconnect`,
      {},
    );
  }

  /** @deprecated - use IntegrationService.connectIntegrationPayment instead */
  connectHobex(
    storeId: number,
    settings: { [key: string]: any },
  ): Observable<ClientStore> {
    return this.http.post<ClientStore>(
      `/api/v2/stores/${storeId}/hobex/connect`,
      settings,
    );
  }

  /** @deprecated - use IntegrationService.disConnectIntegrationPayment instead */
  disconnectHobex(storeId: number): Observable<ClientStore> {
    return this.http.put<ClientStore>(
      `/api/v2/stores/${storeId}/hobex/disconnect`,
      {},
    );
  }

  /** @deprecated - use IntegrationService.connectIntegrationPayment instead */
  connectRyft(
    storeId: number,
    settings: { [key: string]: any },
  ): Observable<ClientStore> {
    return this.http.post<ClientStore>(
      `/api/v2/stores/${storeId}/ryft/connect`,
      settings,
    );
  }

  /** @deprecated - use IntegrationService.disConnectIntegrationPayment instead */
  disconnectRyft(storeId: number): Observable<ClientStore> {
    return this.http.put<ClientStore>(
      `/api/v2/stores/${storeId}/ryft/disconnect`,
      {},
    );
  }

  /** @deprecated - use IntegrationService.connectIntegrationPayment instead */
  connectAbaPayway(
    storeId: number,
    settings: { [key: string]: any },
  ): Observable<ClientStore> {
    return this.http.post<ClientStore>(
      `/api/v2/stores/${storeId}/abapayway/connect`,
      settings,
    );
  }

  /** @deprecated - use IntegrationService.disConnectIntegrationPayment instead */
  disConnectAbaPayway(storeId: number): Observable<ClientStore> {
    return this.http.put<ClientStore>(
      `/api/v2/stores/${storeId}/abapayway/disconnect`,
      {},
    );
  }

  /** @deprecated - use IntegrationService.connectIntegrationPayment instead */
  connectRootline(
    storeId: number,
    settings: { [key: string]: any },
  ): Observable<ClientStore> {
    return this.http.post<ClientStore>(
      `/api/v2/stores/${storeId}/rootline/connect`,
      settings,
    );
  }

  /** @deprecated - use IntegrationService.disConnectIntegrationPayment instead */
  disConnectRootline(storeId: number): Observable<ClientStore> {
    return this.http.put<ClientStore>(
      `/api/v2/stores/${storeId}/rootline/disconnect`,
      {},
    );
  }

  /** @deprecated - use IntegrationService.connectIntegrationPayment instead */
  connectSolidgate(
    storeId: number,
    settings: { [key: string]: any },
  ): Observable<ClientStore> {
    return this.http.post<ClientStore>(
      `/api/v2/stores/${storeId}/solidgate/connect`,
      settings,
    );
  }

  /** @deprecated - use IntegrationService.disConnectIntegrationPayment instead */
  disConnectSolidgate(storeId: number): Observable<ClientStore> {
    return this.http.put<ClientStore>(
      `/api/v2/stores/${storeId}/solidgate/disconnect`,
      {},
    );
  }

  /** @deprecated - use IntegrationService.connectIntegrationPayment instead */
  connectEmerchantPay(
    storeId: number,
    settings: { [key: string]: any },
  ): Observable<ClientStore> {
    return this.http.post<ClientStore>(
      `/api/v2/stores/${storeId}/emerchantpay/connect`,
      settings,
    );
  }

  /** @deprecated - use IntegrationService.disConnectIntegrationPayment instead */
  disConnectEmerchantPay(storeId: number): Observable<ClientStore> {
    return this.http.put<ClientStore>(
      `/api/v2/stores/${storeId}/emerchantpay/disconnect`,
      {},
    );
  }

  updateStoreRelation(
    storeId: number,
    storeRelationRequest: StoreRelationRequest,
    isUpdate = false,
  ): Observable<any> {
    const url = `/api/v1/stores/${storeId}/relation`;
    return !!isUpdate
      ? this.http.patch<any>(url, storeRelationRequest)
      : this.http.post<any>(url, storeRelationRequest);
  }

  validateCustomEndPoint(
    storeId: number,
    method: string,
    customEndPointValidationRequest: CustomEndPointValidationRequest,
  ): Observable<any> {
    if (method === 'get') {
      return this.http.get<any>(
        `/api/v1/stores/${storeId}/orders/events/configs`,
      );
    } else if (method === 'post') {
      return this.http.post<any>(
        `/api/v1/stores/${storeId}/orders/events/configs`,
        customEndPointValidationRequest,
      );
    }
    return this.http.delete<any>(
      `/api/v1/stores/${storeId}/orders/events/configs`,
    );
  }

  changeAccountManager(storeId: number, userId: number): Observable<any> {
    return this.http.post<any>(
      `api/v1/users/${storeId}/changeAccountManager/${userId}`,
      {},
    );
  }

  //START Printer methods ----------------------------------------------------------------------------------------------------------------------------------//
  setStorage(key: string, value: any) {
    this.mobileStorageService?.set(key, value);
  }

  setStorageForPrinters(key: string, value: any) {
    this.getStorage(key)
      .then((existingPrinters) => {
        if (!!existingPrinters && Array.isArray(existingPrinters)) {
          const index = existingPrinters.findIndex(
            (printer) => printer.url == value.url,
          );
          if (index == -1) {
            existingPrinters.push(value);
          } else {
            existingPrinters[index] = value;
          }
          this.mobileStorageService?.set(key, existingPrinters);
        } else {
          this.mobileStorageService?.set(key, [value]);
        }
      })
      .catch((err) => {
        this.mobileStorageService?.set(key, [value]);
        console.error(err);
      });
  }

  removeStorageForPrinters(key: string, printerName: string) {
    this.getStorage(key)
      .then((existingPrinters) => {
        if (!!existingPrinters && Array.isArray(existingPrinters)) {
          const newPrinterList = existingPrinters.filter(
            (printer) => printer.name !== printerName,
          );
          this.mobileStorageService?.set(key, newPrinterList);
        } else {
          this.mobileStorageService?.set(key, []);
          console.error('No Printers are enabled !!');
        }
      })
      .catch((err) => {
        this.mobileStorageService?.set(key, []);
        console.error('No Printers are enabled !!');
        console.error(err);
      });
  }

  async getStorage(key: string) {
    return await this.mobileStorageService.get(key);
  }

  async getStorageForStoreId(key: string, storeId: number): Promise<boolean> {
    return await this.getStorage(key).then((subscriptions) => {
      if (!!storeId && !!subscriptions)
        return subscriptions[`${storeId}`] || false;
      else return false;
    });
  }

  loadMobileNotificationSubscriptionStatus(
    storeId: number,
  ): Observable<boolean> {
    return from(
      this.getStorageForStoreId('notificationSubscriptions', storeId),
    );
  }

  subscribeMobileNotification(
    storeId: number,
    storeAlias: string,
  ): Observable<void> {
    if (storeId <= 0 || !storeAlias) {
      return;
    }
    from(this.mobileStorageService.get('notificationSubscriptions'))
      .pipe(take(1))
      .subscribe((subscriptions) => {
        this.setStorage(
          'notificationSubscriptions',
          this.mobilePushNotificationService.addSubscription(
            storeId,
            storeAlias,
            subscriptions,
          ),
        );
      });
    return new Observable<void>((observer) => observer.complete());
  }

  unsubscribeMobileNotification(storeId: number): Observable<void> {
    from(this.mobileStorageService.get('notificationSubscriptions'))
      .pipe(take(1))
      .subscribe((subscriptions) => {
        this.setStorage(
          'notificationSubscriptions',
          this.mobilePushNotificationService.removeSubscription(
            storeId,
            subscriptions,
          ),
        );
      });
    this.unsubscribeAutoprint(storeId);
    return new Observable<void>((observer) => observer.complete());
  }

  loadAutoprintSubscriptionStatus(storeId: number): Observable<boolean> {
    return from(
      this.getStorageForStoreId('autoprintSubscriptions', storeId),
    ).pipe();
  }

  subscribeAutoprint(storeId: number): Observable<void> {
    if (storeId <= 0) {
      return;
    }
    from(this.mobileStorageService.get('autoprintSubscriptions'))
      .pipe(take(1))
      .subscribe((subscriptions) => {
        this.setStorage(
          'autoprintSubscriptions',
          this.printerService.addSubscription(storeId, subscriptions),
        );
      });
    return new Observable<void>((observer) => observer.complete());
  }

  unsubscribeAutoprint(storeId: number): Observable<void> {
    from(this.mobileStorageService.get('autoprintSubscriptions'))
      .pipe(take(1))
      .subscribe((subscriptions) => {
        this.setStorage(
          'autoprintSubscriptions',
          this.printerService.removeSubscription(storeId, subscriptions),
        );
      });
    return new Observable<void>((observer) => observer.complete());
  }

  findCustomer(
    storeId: number,
    customerParams: any,
  ): Observable<CustomerBasic> {
    return this.http.post<CustomerBasic>(
      `/api/v1/stores/${storeId}/customers/details`,
      { ...customerParams },
    );
  }

  getCustomerOrderSummary(
    storeId: number,
    customerParams: any,
  ): Observable<CustomerOrderSummary> {
    return this.http.post<CustomerOrderSummary>(
      `/api/v1/stores/${storeId}/customers/order-statistics`,
      { ...customerParams },
    );
  }

  getCustomerReservationStatistics(
    storeId: number,
    customerParams: any,
  ): Observable<CustomerReservationStatistics> {
    return this.http.post<CustomerReservationStatistics>(
      `/api/v1/stores/${storeId}/customers/reservation-statistics`,
      { ...customerParams },
    );
  }

  getCustomerLoyaltyProfile(
    storeId: number,
    customerParams: any,
  ): Observable<CustomerLoyaltyProfile> {
    return this.http.post<CustomerLoyaltyProfile>(
      `/api/v1/stores/${storeId}/customers/loyalty-profile`,
      { ...customerParams },
    );
  }

  updateCustomerLoyaltyProfileUpdate(
    storeId: number,
    customerId: number,
    loyaltyData: CustomerLoyaltyProfileUpdateData,
  ): Observable<CustomerLoyaltyProfileUpdate> {
    return this.http.put<CustomerLoyaltyProfileUpdate>(
      `/api/v1/stores/${storeId}/customers/${customerId}/points`,
      loyaltyData,
    );
  }

  getCustomerLoyaltyTransactions(
    storeId: number,
    customerParams: any,
    size: number,
    sortingColumn: string,
    paging = defaultPaging,
  ): Observable<Page<CustomerLoyaltyTrasaction>> {
    return this.http.post<Page<CustomerLoyaltyTrasaction>>(
      `/api/v1/stores/${storeId}/customers/loyalty-transactions?sort=${sortingColumn}&page=${paging.page}&size=${paging.size}`,
      { ...customerParams },
    );
  }

  getCustomerOrderItem(
    storeId: number,
    customerParams: any,
    size: number,
    sortingColumn: string,
  ): Observable<CustomerOrderItems> {
    return this.http.post<CustomerOrderItems>(
      `/api/v1/stores/${storeId}/customers/order-items?sort=${sortingColumn}&size=${size}`,
      { ...customerParams },
    );
  }

  getCustomerOrderItemList(
    customerParams: any,
    storeId: number,
    size: number,
    sortingColumn: string,
    paging = defaultPaging,
    parentOrderItemsOnly?: boolean,
  ): Observable<PageableResults<CustomerOrderItem>> {
    if (parentOrderItemsOnly) {
      const storeApiUrl = `/api/v1/stores/${storeId}/customers/order-items?sort=${sortingColumn}&page=${paging.page}&size=${size}`;
      return this.http.post<PageableResults<CustomerOrderItem>>(storeApiUrl, {
        ...customerParams,
      });
    } else {
      return this.http.post<PageableResults<CustomerOrderItem>>(
        `/api/v1/stores/${storeId}/customers/order-items?sort=${sortingColumn}&page=${paging.page}&size=${size}&parentOrderItemsOnly=true`,
        { ...customerParams },
      );
    }
  }

  getAssignedCustomerVouchers(
    storeId: number,
    customerId: number,
    paging: Paging = { page: 0, size: 10 },
    availableOnly: boolean = false,
  ): Observable<PageableResults<CustomerAssignedVoucher>> {
    const params = new HttpParams()
      .set('page', paging.page.toString())
      .set('size', paging.size.toString())
      .set('availableOnly', availableOnly.toString());

    return this.http.get<PageableResults<CustomerAssignedVoucher>>(
      `/api/v1/stores/${storeId}/customers/${customerId}/customer-vouchers`,
      { params },
    );
  }

  assignCustomerVoucher(
    storeId: number,
    customerId: number,
    voucherCode: string,
  ): Observable<CustomerAssignedVoucher> {
    return this.http.post<CustomerAssignedVoucher>(
      `/api/v1/stores/${storeId}/customers/${customerId}/customer-vouchers/assign`,
      { voucherCode },
    );
  }

  parsePaggingResponse(response) {
    const responseObj = response ? { ...response } : {};
    return {
      data: responseObj.data || [],
      content: responseObj.content || [],
      totalPages: responseObj.totalPages || 0,
      totalElements: responseObj.totalElements || 0,
      pageNumber: responseObj.number || 0,
      pageSize: responseObj.size || 0,
    };
  }

  getCustomerRecentOrders(
    storeId: number,
    customerParams: any,
    size: number,
    sortingColumn: string,
    paging = defaultPaging,
  ): Observable<Page<OrderItem>> {
    return this.http.post<Page<OrderItem>>(
      `/api/v1/stores/${storeId}/customers/orders?sort=${sortingColumn}&page=${paging.page}&size=${paging.size}`,
      { ...customerParams },
    );
  }

  getCustomerReservationList(
    customerParams: any,
    storeId: number,
    size: number,
    sortingColumn: string,
    paging = defaultPaging,
  ): Observable<Page<CustomerReservation>> {
    return this.http.post<Page<CustomerReservation>>(
      `/api/v1/stores/${storeId}/customers/reservations?sort=${sortingColumn}&page=${paging.page}&size=${paging.size}`,
      { ...customerParams },
    );
  }

  postCustomerForm(
    id: number,
    storeId: number,
    formPostParam: CustomerForm,
  ): Observable<any> {
    if (id && id > 0) {
      return this.http.put<any>(`/api/v1/stores/${storeId}/customers/${id}`, {
        ...formPostParam,
      });
    } else {
      return this.http.post<any>(`/api/v1/stores/${storeId}/customers/create`, {
        ...formPostParam,
      });
    }
  }

  checkCompletePaymentStatus(completePaymentUrl: string) {
    return this.http.post(completePaymentUrl, {});
  }

  eposNowSyncCatalog(storeId: number): Observable<any> {
    return this.http.post<any>(
      `/api/v1/stores/${storeId}/eposnow/catalog/import`,
      {},
    );
  }

  getEposNowCategories(storeId: number): Observable<any[]> {
    return this.http.get<any[]>(
      `/api/v1/stores/${storeId}/eposnow/catalog/category`,
      {},
    );
  }

  saveZonePosition(
    zones: SaveZonePositionView,
    storeId,
  ): Observable<SaveZonePositionView> {
    return this.http.post<SaveZonePositionView>(
      `/api/v2/stores/${storeId}/zones/position`,
      zones,
    );
  }

  verifyCustomerData(file, storeId): Observable<any[]> {
    const formdata = new FormData();
    formdata.append('file', file);
    return this.http.post<any[]>(
      `/api/v1/stores/${storeId}/customers/verify`,
      formdata,
    );
  }

  uploadCustomerData(storeId: number, payload: any[]): Observable<void> {
    return this.http.post<void>(
      `/api/v1/stores/${storeId}/customers/upload`,
      payload,
    );
  }

  importCustomerFile(file, storeId): Observable<any> {
    const formdata = new FormData();
    formdata.append('file', file);
    return this.http.post<any>(
      `/api/v1/stores/${storeId}/customers/import-file`,
      formdata,
    );
  }

  uploadStoreKioskVideo(
    storeId: number,
    file: File,
    uploadFileType: string,
    storeSetting: string,
  ): Observable<UploadResponse> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('storeSetting', storeSetting);
    formData.append('uploadFileType', uploadFileType);
    return this.http.post<UploadResponse>(
      `/api/v2/stores/${storeId}/upload-video`,
      formData,
    );
  }

  tisslSyncCatalog(storeId: number): Observable<any> {
    return this.http.post<any>(
      `/api/v1/stores/${storeId}/tissl/catalog/import`,
      {},
    );
  }

  posIntegrationCatalogImport(
    storeId: number,
    integrationProvider: IntegrationProvider,
  ): Observable<any> {
    return this.http.post<any>(
      `/api/v1/stores/${storeId}/${IntegrationProvider[integrationProvider]}/catalog/import`,
      {},
    );
  }

  // Product API methods
  /**
   * Load products for a catalog
   * @param storeId - The store ID
   * @param catalogId - The catalog ID
   * @param activeOnly - Optional filter to return only active products.
   *                     If undefined, backend returns all products (both active and inactive).
   *                     Pass true to get only active products, false to get only inactive products.
   * @returns Observable of ProductListResponse array (minimal product data for listing)
   */
  loadProducts(
    storeId: number,
    catalogId: number,
    activeOnly?: boolean,
  ): Observable<ProductListResponse[]> {
    let params: { activeOnly?: string } = {};
    if (activeOnly !== undefined) {
      params.activeOnly = activeOnly.toString();
    }
    return this.http.get<ProductListResponse[]>(
      `/api/v1/stores/${storeId}/catalog/${catalogId}/products`,
      { params },
    );
  }

  /**
   * Load linkable products for a catalog
   * Used for searching and linking products between catalogs
   * @param storeId - The store ID
   * @param catalogId - The catalog ID
   * @returns Observable of ProductSearchResponse array (product data with more details for linking)
   */
  loadLinkableProducts(
    storeId: number,
    catalogId: number,
  ): Observable<ProductSearchResponse[]> {
    return this.http.get<ProductSearchResponse[]>(
      `/api/v1/stores/${storeId}/catalog/${catalogId}/products/linkable`,
    );
  }

  /**
   * Get a single product by ID
   * @param storeId - The store ID
   * @param catalogId - The catalog ID
   * @param productId - The product ID
   * @returns Observable of ProductResponse (full product details)
   */
  getProduct(
    storeId: number,
    catalogId: number,
    productId: number,
  ): Observable<ProductResponse> {
    return this.http.get<ProductResponse>(
      `/api/v1/stores/${storeId}/catalog/${catalogId}/products/${productId}`,
    );
  }

  /**
   * Create a new product
   * @param storeId - The store ID
   * @param catalogId - The catalog ID
   * @param product - The product request data
   * @returns Observable of ProductResponse (created product details)
   */
  createProduct(
    storeId: number,
    catalogId: number,
    product: ProductRequest,
  ): Observable<ProductResponse> {
    return this.http.post<ProductResponse>(
      `/api/v1/stores/${storeId}/catalog/${catalogId}/products`,
      {
        name: product.name,
        shortDescription: product.shortDescription,
        longDescription: product.longDescription,
        price: product.price,
        vatPercentage: product.vatPercentage,
        externalProductId: product.externalProductId,
        vatExternalId: product.vatExternalId,
        isActive: product.isActive,
        attributeDtos: product.attributeDtos || [],
        imageUrl: product.imageUrl,
        calories: product.calories,
        protein: product.protein,
        netCarbs: product.netCarbs,
        totalFats: product.totalFats,
      },
    );
  }

  /**
   * Update an existing product
   * @param storeId - The store ID
   * @param catalogId - The catalog ID
   * @param productId - The product ID
   * @param product - The product request data
   * @returns Observable of ProductResponse (updated product details)
   */
  updateProduct(
    storeId: number,
    catalogId: number,
    productId: number,
    product: ProductRequest,
  ): Observable<ProductResponse> {
    return this.http.put<ProductResponse>(
      `/api/v1/stores/${storeId}/catalog/${catalogId}/products/${productId}`,
      {
        name: product.name,
        shortDescription: product.shortDescription,
        longDescription: product.longDescription,
        price: product.price,
        vatPercentage: product.vatPercentage,
        externalProductId: product.externalProductId,
        vatExternalId: product.vatExternalId,
        isActive: product.isActive,
        attributeDtos: product.attributeDtos || [],
        imageUrl: product.imageUrl,
        calories: product.calories,
        protein: product.protein,
        netCarbs: product.netCarbs,
        totalFats: product.totalFats,
      },
    );
  }

  /**
   * Delete a product
   * @param storeId - The store ID
   * @param catalogId - The catalog ID
   * @param productId - The product ID
   * @returns Observable of void
   */
  deleteProduct(
    storeId: number,
    catalogId: number,
    productId: number,
  ): Observable<void> {
    return this.http.delete<void>(
      `/api/v1/stores/${storeId}/catalog/${catalogId}/products/${productId}`,
    );
  }

  generateCode(storeId: number): Observable<{ code: string }> {
    if (!Number.isFinite(storeId) || storeId <= 0) {
      return throwError(
        () => new Error('storeId is required for generateCode'),
      );
    }
    return this.http.post<{ code: string }>(
      `/api/v1/stores/${storeId}/generate-code`,
      {},
    );
  }

  inviteUserToRegister(storeId: number, customerId: number) {
    return this.http.post<void>(
      `/api/v1/stores/${storeId}/customers/${customerId}/invite`,
      {},
    );
  }

  setHierarchyType(storeId: number, hierarchyType: string): Observable<void> {
    return this.http.put<void>(
      `/api/v1/stores/${storeId}/relation/hierarchy-type`,
      { hierarchyType },
    );
  }
}
